import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Loader2, Check, AlertCircle, Plus, Trash2, ChevronDown, ChevronUp, Mail, Settings, FileText, Eye, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EditableField = ({ label, value, onChange, type = "text", rows = 1, placeholder = "" }) => {
  if (type === "textarea" || rows > 1) {
    return (
      <div className="mb-4">
        <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-2">
          {label}
        </label>
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className="w-full bg-brand-black border border-white/20 text-white px-4 py-3 font-montserrat focus:border-brand-lime focus:outline-none transition-colors resize-none"
        />
      </div>
    );
  }
  
  return (
    <div className="mb-4">
      <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-brand-black border border-white/20 text-white px-4 py-3 font-montserrat focus:border-brand-lime focus:outline-none transition-colors"
      />
    </div>
  );
};

const CollapsibleSection = ({ title, children, defaultOpen = false, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-brand-lime/20 mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-brand-dark hover:bg-brand-dark/80 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="font-oswald text-lg uppercase tracking-wider text-white">{title}</span>
          {badge && (
            <span className="bg-brand-lime/20 text-brand-lime text-xs px-2 py-1 font-oswald uppercase">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? <ChevronUp className="text-brand-lime" /> : <ChevronDown className="text-brand-lime" />}
      </button>
      {isOpen && (
        <div className="p-6 bg-brand-black">
          {children}
        </div>
      )}
    </div>
  );
};

export const FormSettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');

  const formId = 'choose-you-intake';

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      // Fetch form settings
      const settingsRes = await fetch(`${API_URL}/api/admin/forms/${formId}/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(data || getDefaultSettings());
      } else {
        setSettings(getDefaultSettings());
      }

      // Fetch submissions
      const submissionsRes = await fetch(`${API_URL}/api/admin/forms/submissions?form_id=${formId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (submissionsRes.ok) {
        const data = await submissionsRes.json();
        setSubmissions(data || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setSettings(getDefaultSettings());
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultSettings = () => ({
    form_id: formId,
    name: "Choose You 30-Day Series Intake Form",
    admin_email: "PowerUpYourFitness@gmail.com",
    email_notifications: true,
    email_subject: "New Choose You Application",
    confirmation_title: "Application",
    confirmation_title_accent: "Received!",
    confirmation_message: "Your spot is reserved pending payment.",
    form_title: "Apply for",
    form_title_accent: "Choose You",
    form_subtitle: "30-Day Series - Starts March 2",
    submit_button_text: "Apply for Choose You",
    fields: [
      { id: "firstName", label: "First Name", type: "text", required: true },
      { id: "lastName", label: "Last Name", type: "text", required: true },
      { id: "email", label: "Email", type: "email", required: true },
      { id: "phone", label: "Phone Number", type: "tel", required: true },
      { id: "optionSelected", label: "Option Selected", type: "select", required: true },
      { id: "notes", label: "Notes", type: "textarea", required: false }
    ],
    payment_info: {
      show_payment: true,
      cash_app: "$PowerUpYourFitness",
      zelle: "PowerUpYourFitness@gmail.com",
      proof_note: "Proof of payment must be emailed to PowerUpYourFitness@gmail.com"
    }
  });

  const updateSettings = (path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
    setSaveStatus(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/forms/${formId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setSaveStatus('success');
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus('error');
      }
    } catch (err) {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const markAsRead = async (submissionId) => {
    const token = localStorage.getItem('adminToken');
    await fetch(`${API_URL}/api/admin/forms/submissions/${submissionId}/read`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    setSubmissions(prev => prev.map(s => s.id === submissionId ? { ...s, read: true } : s));
  };

  const deleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    const token = localStorage.getItem('adminToken');
    await fetch(`${API_URL}/api/admin/forms/submissions/${submissionId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    setSubmissions(prev => prev.filter(s => s.id !== submissionId));
  };

  const exportSubmissions = () => {
    const csv = [
      ['Date', 'First Name', 'Last Name', 'Email', 'Phone', 'Option', 'Notes', 'Status'].join(','),
      ...submissions.map(s => [
        new Date(s.submitted_at).toLocaleString(),
        s.data?.firstName || '',
        s.data?.lastName || '',
        s.data?.email || '',
        s.data?.phone || '',
        s.data?.optionSelected || '',
        (s.data?.notes || '').replace(/,/g, ';'),
        s.read ? 'Read' : 'New'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `choose-you-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-brand-lime animate-spin" />
      </div>
    );
  }

  const unreadCount = submissions.filter(s => !s.read).length;

  return (
    <div className="min-h-screen bg-brand-black pt-20 pb-12">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 text-brand-gray hover:text-white transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="font-oswald text-3xl uppercase text-white">
                Choose You <span className="text-brand-lime">Intake Form</span>
              </h1>
              <p className="font-montserrat text-sm text-brand-gray">
                Manage form settings and view submissions
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex items-center gap-2 px-6 py-3 font-oswald uppercase tracking-wider transition-colors ${
              activeTab === 'settings' 
                ? 'bg-brand-lime text-brand-black' 
                : 'bg-brand-dark text-brand-gray hover:text-white'
            }`}
          >
            <Settings size={18} /> Settings
          </button>
          <button
            onClick={() => setActiveTab('submissions')}
            className={`flex items-center gap-2 px-6 py-3 font-oswald uppercase tracking-wider transition-colors relative ${
              activeTab === 'submissions' 
                ? 'bg-brand-lime text-brand-black' 
                : 'bg-brand-dark text-brand-gray hover:text-white'
            }`}
          >
            <FileText size={18} /> Submissions
            {unreadCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-brand-pink text-white text-xs w-6 h-6 rounded-full flex items-center justify-center font-oswald">
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'settings' && (
          <>
            {/* Save Status */}
            {saveStatus && (
              <div className={`mb-6 p-4 border ${saveStatus === 'success' ? 'border-brand-lime/30 bg-brand-lime/10' : 'border-red-500/30 bg-red-500/10'}`}>
                <p className={`font-montserrat ${saveStatus === 'success' ? 'text-brand-lime' : 'text-red-400'}`}>
                  {saveStatus === 'success' ? 'Settings saved successfully!' : 'Failed to save. Please try again.'}
                </p>
              </div>
            )}

            {/* Email & Notification Settings */}
            <CollapsibleSection title="Email & Notifications" defaultOpen={true} badge="Important">
              <div className="flex items-center gap-4 mb-6">
                <Mail className="text-brand-lime" size={24} />
                <div className="flex-1">
                  <label className="block font-oswald text-sm uppercase tracking-wider text-white mb-1">
                    Email Notifications
                  </label>
                  <p className="text-brand-gray text-sm">Receive notifications when forms are submitted</p>
                </div>
                <button
                  onClick={() => updateSettings('email_notifications', !settings.email_notifications)}
                  className={`w-14 h-8 rounded-full transition-colors ${settings.email_notifications ? 'bg-brand-lime' : 'bg-brand-gray/30'}`}
                >
                  <div className={`w-6 h-6 bg-white rounded-full transition-transform ${settings.email_notifications ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>
              
              <EditableField 
                label="Admin Email (receives notifications)" 
                value={settings.admin_email} 
                onChange={(val) => updateSettings('admin_email', val)}
                type="email"
              />
              
              <EditableField 
                label="Email Subject Line" 
                value={settings.email_subject} 
                onChange={(val) => updateSettings('email_subject', val)}
              />
            </CollapsibleSection>

            {/* Form Display Settings */}
            <CollapsibleSection title="Form Display">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <EditableField 
                  label="Form Title" 
                  value={settings.form_title} 
                  onChange={(val) => updateSettings('form_title', val)}
                />
                <EditableField 
                  label="Form Title Accent (Green)" 
                  value={settings.form_title_accent} 
                  onChange={(val) => updateSettings('form_title_accent', val)}
                />
              </div>
              <EditableField 
                label="Form Subtitle" 
                value={settings.form_subtitle} 
                onChange={(val) => updateSettings('form_subtitle', val)}
              />
              <EditableField 
                label="Submit Button Text" 
                value={settings.submit_button_text} 
                onChange={(val) => updateSettings('submit_button_text', val)}
              />
            </CollapsibleSection>

            {/* Confirmation Message */}
            <CollapsibleSection title="Confirmation Message">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <EditableField 
                  label="Confirmation Title" 
                  value={settings.confirmation_title} 
                  onChange={(val) => updateSettings('confirmation_title', val)}
                />
                <EditableField 
                  label="Confirmation Title Accent" 
                  value={settings.confirmation_title_accent} 
                  onChange={(val) => updateSettings('confirmation_title_accent', val)}
                />
              </div>
              <EditableField 
                label="Confirmation Message" 
                value={settings.confirmation_message} 
                onChange={(val) => updateSettings('confirmation_message', val)}
                rows={3}
              />
            </CollapsibleSection>

            {/* Payment Information */}
            <CollapsibleSection title="Payment Information">
              <div className="flex items-center gap-4 mb-6">
                <div className="flex-1">
                  <label className="block font-oswald text-sm uppercase tracking-wider text-white mb-1">
                    Show Payment Details
                  </label>
                  <p className="text-brand-gray text-sm">Display payment info after form submission</p>
                </div>
                <button
                  onClick={() => updateSettings('payment_info.show_payment', !settings.payment_info?.show_payment)}
                  className={`w-14 h-8 rounded-full transition-colors ${settings.payment_info?.show_payment ? 'bg-brand-lime' : 'bg-brand-gray/30'}`}
                >
                  <div className={`w-6 h-6 bg-white rounded-full transition-transform ${settings.payment_info?.show_payment ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <EditableField 
                  label="Cash App" 
                  value={settings.payment_info?.cash_app} 
                  onChange={(val) => updateSettings('payment_info.cash_app', val)}
                />
                <EditableField 
                  label="Zelle" 
                  value={settings.payment_info?.zelle} 
                  onChange={(val) => updateSettings('payment_info.zelle', val)}
                />
              </div>
              <EditableField 
                label="Payment Proof Note" 
                value={settings.payment_info?.proof_note} 
                onChange={(val) => updateSettings('payment_info.proof_note', val)}
                rows={2}
              />
            </CollapsibleSection>

            {/* Form Fields */}
            <CollapsibleSection title="Form Fields">
              <p className="text-brand-gray mb-4">Configure the fields that appear in the intake form.</p>
              
              {settings.fields?.map((field, index) => (
                <div key={field.id} className="bg-brand-dark border border-white/10 p-4 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-oswald text-white uppercase">{field.label}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 ${field.required ? 'bg-brand-pink/20 text-brand-pink' : 'bg-brand-gray/20 text-brand-gray'}`}>
                        {field.required ? 'Required' : 'Optional'}
                      </span>
                      <span className="text-xs px-2 py-1 bg-brand-lime/20 text-brand-lime">{field.type}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-brand-gray mb-1">Label</label>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => {
                          const newFields = [...settings.fields];
                          newFields[index].label = e.target.value;
                          updateSettings('fields', newFields);
                        }}
                        className="w-full bg-brand-black border border-white/20 text-white px-3 py-2 text-sm focus:border-brand-lime focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-brand-gray mb-1">Placeholder</label>
                      <input
                        type="text"
                        value={field.placeholder || ''}
                        onChange={(e) => {
                          const newFields = [...settings.fields];
                          newFields[index].placeholder = e.target.value;
                          updateSettings('fields', newFields);
                        }}
                        className="w-full bg-brand-black border border-white/20 text-white px-3 py-2 text-sm focus:border-brand-lime focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CollapsibleSection>

            {/* Save Button */}
            <div className="mt-8 text-center">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center gap-2 bg-brand-lime text-brand-black px-10 py-4 font-oswald uppercase tracking-wider text-lg hover:bg-brand-pink hover:text-white transition-all disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </>
        )}

        {activeTab === 'submissions' && (
          <div className="space-y-6">
            {/* Submissions Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-oswald text-2xl uppercase text-white">Form Submissions</h2>
                <p className="text-brand-gray">{submissions.length} total submissions, {unreadCount} unread</p>
              </div>
              {submissions.length > 0 && (
                <button
                  onClick={exportSubmissions}
                  className="flex items-center gap-2 bg-brand-dark border border-brand-lime/30 text-brand-lime px-4 py-2 font-oswald uppercase text-sm hover:bg-brand-lime hover:text-brand-black transition-colors"
                >
                  <Download size={18} /> Export CSV
                </button>
              )}
            </div>

            {/* Submissions List */}
            {submissions.length === 0 ? (
              <div className="bg-brand-dark border border-white/10 p-12 text-center">
                <FileText size={48} className="text-brand-gray mx-auto mb-4" />
                <h3 className="font-oswald text-xl text-white mb-2">No Submissions Yet</h3>
                <p className="text-brand-gray">Form submissions will appear here when someone applies.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {submissions.map((submission) => (
                  <div 
                    key={submission.id}
                    className={`bg-brand-dark border p-6 ${!submission.read ? 'border-brand-lime/50' : 'border-white/10'}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-oswald text-lg text-white">
                            {submission.data?.firstName} {submission.data?.lastName}
                          </h3>
                          {!submission.read && (
                            <span className="bg-brand-lime text-brand-black text-xs px-2 py-1 font-oswald uppercase">New</span>
                          )}
                        </div>
                        <p className="text-brand-gray text-sm">{submission.data?.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-brand-gray text-sm">
                          {new Date(submission.submitted_at).toLocaleDateString()}
                        </p>
                        <p className="text-brand-gray text-xs">
                          {new Date(submission.submitted_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <span className="text-xs text-brand-gray uppercase">Phone</span>
                        <p className="text-white">{submission.data?.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-brand-gray uppercase">Option Selected</span>
                        <p className="text-brand-lime">{submission.data?.optionSelected === 'chat-only' ? 'Chat Only ($100)' : 'Chat + Workouts ($169)'}</p>
                      </div>
                    </div>
                    
                    {submission.data?.notes && (
                      <div className="mb-4">
                        <span className="text-xs text-brand-gray uppercase">Notes</span>
                        <p className="text-white">{submission.data.notes}</p>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      {!submission.read && (
                        <button
                          onClick={() => markAsRead(submission.id)}
                          className="flex items-center gap-1 text-brand-lime hover:text-white text-sm transition-colors"
                        >
                          <Eye size={14} /> Mark as Read
                        </button>
                      )}
                      <button
                        onClick={() => deleteSubmission(submission.id)}
                        className="flex items-center gap-1 text-red-400 hover:text-red-300 text-sm transition-colors ml-auto"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
